import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaLabels } from '../context/PaLabelsContext';
import { LOGO_SRC } from '../data/logo';
import { GOTO_PAGE_PATHS, pathForGoTo } from '../lib/siteRoutes';
import LanguageSwitcher from './LanguageSwitcher';

export default function Nav() {
  const { t } = useTranslation('common');
  const { navPracticeLinks } = usePaLabels();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleMobile = () => setMobileOpen((v) => !v);

  const goTo = (page) => (e) => {
    e.preventDefault();
    navigate(pathForGoTo(page));
  };

  const goToMobile = (page) => (e) => {
    e.preventDefault();
    navigate(pathForGoTo(page));
    setMobileOpen(false);
  };

  const isActive = (page) => {
    const path = pathForGoTo(page);
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    if (page === 'practices') {
      return (
        location.pathname === GOTO_PAGE_PATHS.practices ||
        location.pathname.startsWith('/practices/')
      );
    }
    return location.pathname === path;
  };

  return (
    <>
      <nav>
        <a href="/" className="nav-logo" onClick={goTo('home')}>
          <img
            src={LOGO_SRC}
            alt="Dilinh Legal"
            width={267}
            height={106}
            style={{ height: '44px', width: 'auto', display: 'block' }}
          />
        </a>
        <ul className="nav-links">
          <li>
            <a
              href="/"
              onClick={goTo('home')}
              data-page="home"
              className={isActive('home') ? 'nav-active' : ''}
            >
              {t('nav.home')}
            </a>
          </li>
          <li className="nav-dropdown">
            <a
              href={GOTO_PAGE_PATHS.practices}
              onClick={goTo('practices')}
              data-page="practices"
              className={isActive('practices') ? 'nav-active' : ''}
            >
              {t('nav.practices')}
            </a>
            <ul className="nav-dropdown-menu">
              {navPracticeLinks.map(({ pageKey, href, label }) => (
                <li key={pageKey}>
                  <a href={href} onClick={goTo(pageKey)}>
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </li>
          <li>
            <a
              href={GOTO_PAGE_PATHS.team}
              onClick={goTo('team')}
              data-page="team"
              className={isActive('team') ? 'nav-active' : ''}
            >
              {t('nav.team')}
            </a>
          </li>
          <li>
            <a
              href={GOTO_PAGE_PATHS.toolkits}
              onClick={goTo('toolkits')}
              data-page="toolkits"
              className={isActive('toolkits') ? 'nav-active' : ''}
            >
              {t('nav.toolkits')}
            </a>
          </li>
          <li>
            <a
              href={GOTO_PAGE_PATHS.updates}
              onClick={goTo('updates')}
              data-page="updates"
              className={isActive('updates') ? 'nav-active' : ''}
            >
              {t('nav.updates')}
            </a>
          </li>
          <li>
            <a
              href={GOTO_PAGE_PATHS.contact}
              onClick={goTo('contact')}
              data-page="contact"
              className={isActive('contact') ? 'nav-active' : ''}
            >
              {t('nav.contact')}
            </a>
          </li>
        </ul>
        <div className="nav-right">
          <LanguageSwitcher />
          <button
            type="button"
            className="nav-hamburger"
            onClick={toggleMobile}
            id="hamburger"
            aria-expanded={mobileOpen}
            aria-controls="mobileNav"
            aria-label={mobileOpen ? t('ui.close') : 'Open menu'}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      <div
        className={`mobile-nav${mobileOpen ? ' open' : ''}`}
        id="mobileNav"
        aria-hidden={!mobileOpen}
      >
        <a href="/" onClick={goToMobile('home')}>
          {t('nav.home')}
        </a>
        <a href={GOTO_PAGE_PATHS.practices} onClick={goToMobile('practices')}>
          {t('nav.practices')}
        </a>
        <div className="mobile-nav-sub">
          {navPracticeLinks.map(({ pageKey, href, label }) => (
            <a key={pageKey} href={href} onClick={goToMobile(pageKey)}>
              {label}
            </a>
          ))}
        </div>
        <a href={GOTO_PAGE_PATHS.team} onClick={goToMobile('team')}>
          {t('nav.team')}
        </a>
        <a href={GOTO_PAGE_PATHS.toolkits} onClick={goToMobile('toolkits')}>
          {t('nav.toolkits')}
        </a>
        <a href={GOTO_PAGE_PATHS.updates} onClick={goToMobile('updates')}>
          {t('nav.updates')}
        </a>
        <a href={GOTO_PAGE_PATHS.contact} onClick={goToMobile('contact')}>
          {t('nav.contact')}
        </a>
        <div style={{ marginTop: '16px' }}>
          <LanguageSwitcher />
        </div>
      </div>
    </>
  );
}
